import { CuppingSessionResult } from '../types/cuppingResult'
import { BasicDataParam, GraphDataParam } from '../types/enums/graphDataParam'
import { EnabledBetaFeatures } from '../types/customer'
import { LAST_EVENT_SERIES, TempViewSeries } from '../types/tempViewSeries'

const AUTH_TOKEN = 'roest-auth-token'
const REFRESH_TOKEN = 'roest-refresh-token'
const USERNAME = 'roest-session-user'
const COMPARE_LOG_PARAMS = 'roest-compare-log-params'
const COMPARE_LIVE_PARAMS = 'roest-compare-live-params'
const CUPPING_ROASTS = 'roest-cupping-roasts'
const COMPLETED_CUPPING_SESSIONS = 'roest-completed-cupping-sessions'
const CUPPING_SESSION_TEMP_SCORES = 'roest-cupping-session-temp-scores'
const CURRENT_CUSTOMER = 'roest-current-customer'
const LAST_SELECTED_MACHINE = 'roest-last-selected-machine'
const IS_FAHRENHEIT = 'roest-is-fahrenheit'
const IS_POUNDS = 'roest-is-pounds'
const ENABLED_BETA_FEATURES = 'roest-enabled-beta-features'
const LOG_VIEW_HIDDEN_SERIES = 'roest-log-view-hidden-series'
const LOG_LIST_HIDDEN_COLUMNS = 'roest-log-list-hidden-columns'
const INVENTORY_LIST_HIDDEN_COLUMNS = 'roest-inventory-list-hidden-columns'
const LIVE_TEMP_SERIES = 'roest-live-temp-series'
const DISMISSED_SYSTEM_MESSAGES = 'roest-dismissed-system-messages'

const parseLocalStorageValue = <T>(key: string): T | null => {
  try {
    const parsedValue = JSON.parse(localStorage.getItem(key) || 'null')
    return parsedValue
  } catch {
    localStorage.removeItem(key) // remove the faulty key
    console.error(`localStorage error parsing ${key}`)
  }
  return null
}

export const getRefreshKey = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN)
}
export const setRefreshKey = (newKey: string): void => {
  localStorage.setItem(REFRESH_TOKEN, newKey)
}

//

export const getTokenKey = (): string | null => {
  return localStorage.getItem(AUTH_TOKEN)
}
export const setTokenKey = (newKey: string): void => {
  localStorage.setItem(AUTH_TOKEN, newKey)
}

//

export const getUsername = (): string | null => {
  return localStorage.getItem(USERNAME)
}
export const setUserName = (username: string): void => {
  localStorage.setItem(USERNAME, username)
}

//

export const setCompareLogParams = (params: Array<GraphDataParam>): void => {
  localStorage.setItem(COMPARE_LOG_PARAMS, JSON.stringify(params))
}

export const getCompareLogParams = (): Array<GraphDataParam> => {
  return parseLocalStorageValue<Array<GraphDataParam>>(COMPARE_LOG_PARAMS) || []
}

//

export const setCompareLiveParams = (params: Array<GraphDataParam>): void => {
  localStorage.setItem(COMPARE_LIVE_PARAMS, JSON.stringify(params))
}

export const getCompareLiveParams = (): Array<GraphDataParam> => {
  return parseLocalStorageValue<Array<GraphDataParam>>(COMPARE_LIVE_PARAMS) || []
}

//

export const setIsFahrenheit = (isFahrenheit: boolean): void => {
  localStorage.setItem(IS_FAHRENHEIT, JSON.stringify(isFahrenheit))
}

export const isFahrenheit = (): boolean => {
  return parseLocalStorageValue<boolean>(IS_FAHRENHEIT) || false
}

//

export const setIsPounds = (isPounds: boolean): void => {
  localStorage.setItem(IS_POUNDS, JSON.stringify(isPounds))
}

export const isPounds = (): boolean => {
  return parseLocalStorageValue<boolean>(IS_POUNDS) || false
}

//

export const setSelectedCuppingRoasts = (roastIds: Array<string>): void => {
  localStorage.setItem(CUPPING_ROASTS, JSON.stringify(roastIds))
}

export const getSelectedCuppingRoasts = (): Array<string> => {
  return parseLocalStorageValue<Array<string>>(CUPPING_ROASTS) || []
}

//

export const setCompletedCuppingSessions = (roastIds: Array<string>): void => {
  localStorage.setItem(COMPLETED_CUPPING_SESSIONS, JSON.stringify(roastIds))
}

export const getCompletedCuppingSessions = (): Array<string> => {
  return parseLocalStorageValue<Array<string>>(COMPLETED_CUPPING_SESSIONS) || []
}

//

export const setTemporaryCuppingScores = (scores: CuppingSessionResult): void => {
  localStorage.setItem(CUPPING_SESSION_TEMP_SCORES, JSON.stringify(scores))
}

export const getTemporaryCuppingScores = (): CuppingSessionResult | null => {
  return parseLocalStorageValue<CuppingSessionResult>(CUPPING_SESSION_TEMP_SCORES) || null
}

//

export const setHiddenChartSeries = (hiddenSeriesIds: Array<string>): void => {
  localStorage.setItem(LOG_VIEW_HIDDEN_SERIES, JSON.stringify(hiddenSeriesIds))
}

export const getHiddenChartSeries = (): Array<string> => {
  return parseLocalStorageValue<Array<string>>(LOG_VIEW_HIDDEN_SERIES) || []
}

//

export const setLogListHiddenColumns = (hiddenColumnIds: Array<string>): void => {
  localStorage.setItem(LOG_LIST_HIDDEN_COLUMNS, JSON.stringify(hiddenColumnIds))
}

export const getLogListHiddenColumns = (): Array<string> => {
  return parseLocalStorageValue<Array<string>>(LOG_LIST_HIDDEN_COLUMNS) || []
}

//

export const setInventoryListHiddenColumns = (hiddenColumnIds: Array<string>): void => {
  localStorage.setItem(INVENTORY_LIST_HIDDEN_COLUMNS, JSON.stringify(hiddenColumnIds))
}

export const getInventoryListHiddenColumns = (): Array<string> => {
  return parseLocalStorageValue<Array<string>>(INVENTORY_LIST_HIDDEN_COLUMNS) || []
}

//

export const setDismissedSystemMessages = (dismissedMessageUrls: Array<string>): void => {
  localStorage.setItem(DISMISSED_SYSTEM_MESSAGES, JSON.stringify(dismissedMessageUrls))
}

export const getDismissedSystemMessages = (): Array<string> => {
  return parseLocalStorageValue<Array<string>>(DISMISSED_SYSTEM_MESSAGES) || []
}

//

export const setLiveViewTempSeries = (tempSeries: Array<TempViewSeries>): void => {
  localStorage.setItem(LIVE_TEMP_SERIES, JSON.stringify(tempSeries))
}

export const getLiveViewTempSeries = (): Array<TempViewSeries> => {
  return (
    parseLocalStorageValue<Array<TempViewSeries>>(LIVE_TEMP_SERIES) || [
      BasicDataParam.BT,
      BasicDataParam.ET,
      LAST_EVENT_SERIES,
    ]
  )
}

//

export const setLastSelectedMachine = (machineUrl: string) => {
  localStorage.setItem(LAST_SELECTED_MACHINE, JSON.stringify(machineUrl))
}

export const getLastSelectedMachine = (): string | null => {
  return parseLocalStorageValue<string>(LAST_SELECTED_MACHINE) || null
}

//

export const setSelectedCustomer = (customerUrl: string) => {
  localStorage.setItem(CURRENT_CUSTOMER, JSON.stringify(customerUrl))
}

export const getSelectedCustomer = (): string | null => {
  return parseLocalStorageValue<string>(CURRENT_CUSTOMER) || null
}

export const setEnabledBetaFeatures = (enabledBetaFeatures: EnabledBetaFeatures) => {
  localStorage.setItem(ENABLED_BETA_FEATURES, JSON.stringify(enabledBetaFeatures))
}

export const hasBetaFeature = (name: string): boolean => {
  const enabledBetaFeatures = parseLocalStorageValue<EnabledBetaFeatures>(ENABLED_BETA_FEATURES) || []
  return enabledBetaFeatures.find((x) => x.name === name) ? true : false
}
